export type Channel = "offline" | "online";
export type PricingTemplate =
  "default" | "europeanDebitSplit" | "domesticCreditSplit";
export type FeeType = "Percentage" | "Fixed";
export type VolSource = "business" | "area" | "manual";

export interface PriceSpec {
  value: number;
  valueType: FeeType;
}
export interface GroupDef {
  label: string;
  buckets: string;
  cost: PriceSpec;
  current: PriceSpec;
}
export type GroupKey =
  | "domesticDebit"
  | "domesticCredit"
  | "europeanDebit"
  | "european"
  | "international"
  | "onlineIdeal"
  | "onlineNonCorp"
  | "onlineCorp";

export const COUNTRIES: Record<string, string> = {
  NL: "🇳🇱 Netherlands",
  BE: "🇧🇪 Belgium",
  DE: "🇩🇪 Germany",
  FR: "🇫🇷 France",
  IT: "🇮🇹 Italy",
};

export const CITY_COORDS: Record<
  string,
  { lat: number; lng: number; label: string; sub: string; f: number }
> = {
  Amsterdam: {
    lat: 52.3676,
    lng: 4.9041,
    label: "Amsterdam",
    sub: "Noord-Holland · 1011 AB",
    f: 1.15,
  },
  Rotterdam: {
    lat: 51.9244,
    lng: 4.4777,
    label: "Rotterdam",
    sub: "Zuid-Holland · 3011",
    f: 1.0,
  },
  "Den Haag": {
    lat: 52.0705,
    lng: 4.3007,
    label: "Den Haag",
    sub: "Zuid-Holland · 2511",
    f: 1.02,
  },
  Utrecht: {
    lat: 52.0907,
    lng: 5.1214,
    label: "Utrecht",
    sub: "Utrecht · 3511",
    f: 1.05,
  },
  Eindhoven: {
    lat: 51.4416,
    lng: 5.4697,
    label: "Eindhoven",
    sub: "Noord-Brabant · 5611",
    f: 0.95,
  },
  Groningen: {
    lat: 53.2194,
    lng: 6.5665,
    label: "Groningen",
    sub: "Groningen · 9711",
    f: 0.88,
  },
};

export const INDUSTRY_GROUPS = [
  "Restaurants & Bars",
  "Retail Shops",
  "Service Providers",
  "Beauty & Barber",
  "Entertainment & Events",
  "Taxi & Transportation",
  "Other",
];

export const MCC_DATA: Record<string, string> = {
  "4121": "Taxi & Transportation",
  "4215": "Taxi & Transportation",
  "4722": "Taxi & Transportation",
  "4789": "Taxi & Transportation",
  "5200": "Retail Shops",
  "5311": "Retail Shops",
  "5411": "Restaurants & Bars",
  "5422": "Restaurants & Bars",
  "5441": "Restaurants & Bars",
  "5451": "Restaurants & Bars",
  "5462": "Restaurants & Bars",
  "5499": "Restaurants & Bars",
  "5611": "Retail Shops",
  "5621": "Retail Shops",
  "5631": "Retail Shops",
  "5641": "Retail Shops",
  "5651": "Retail Shops",
  "5661": "Retail Shops",
  "5699": "Retail Shops",
  "5712": "Retail Shops",
  "5722": "Retail Shops",
  "5732": "Retail Shops",
  "5811": "Restaurants & Bars",
  "5812": "Restaurants & Bars",
  "5813": "Restaurants & Bars",
  "5814": "Restaurants & Bars",
  "5912": "Retail Shops",
  "5921": "Restaurants & Bars",
  "5941": "Retail Shops",
  "5942": "Retail Shops",
  "5944": "Retail Shops",
  "5945": "Retail Shops",
  "5947": "Retail Shops",
  "5977": "Retail Shops",
  "5992": "Retail Shops",
  "5995": "Retail Shops",
  "7011": "Other",
  "7032": "Entertainment & Events",
  "7033": "Entertainment & Events",
  "7210": "Service Providers",
  "7211": "Service Providers",
  "7216": "Service Providers",
  "7217": "Service Providers",
  "7221": "Service Providers",
  "7230": "Beauty & Barber",
  "7251": "Service Providers",
  "7276": "Service Providers",
  "7277": "Service Providers",
  "7297": "Beauty & Barber",
  "7298": "Beauty & Barber",
  "7299": "Service Providers",
  "7349": "Service Providers",
  "7372": "Service Providers",
  "7392": "Service Providers",
  "7512": "Service Providers",
  "7523": "Other",
  "7538": "Other",
  "7622": "Retail Shops",
  "7832": "Entertainment & Events",
  "7922": "Entertainment & Events",
  "7929": "Entertainment & Events",
  "7932": "Entertainment & Events",
  "7991": "Entertainment & Events",
  "7992": "Entertainment & Events",
  "7994": "Entertainment & Events",
  "7995": "Entertainment & Events",
  "7999": "Entertainment & Events",
  "8011": "Service Providers",
  "8021": "Service Providers",
  "8031": "Service Providers",
  "8099": "Service Providers",
  "8111": "Service Providers",
  "8299": "Service Providers",
  "8911": "Service Providers",
  "8931": "Service Providers",
};

export const MCC_NAMES: Record<string, string> = {
  "4121": "Taxicabs & Limousines",
  "4215": "Courier Services",
  "4722": "Travel Agencies",
  "4789": "Transportation Services",
  "5200": "Home Supply Warehouse",
  "5311": "Department Stores",
  "5411": "Grocery Stores & Supermarkets",
  "5422": "Meat Provisioners",
  "5441": "Candy & Confectionery",
  "5451": "Dairy Products Stores",
  "5462": "Bakeries",
  "5499": "Specialty Food Stores",
  "5611": "Men's Clothing",
  "5621": "Women's Ready-to-Wear",
  "5631": "Women's Accessories",
  "5641": "Children's & Infants' Wear",
  "5651": "Family Clothing Stores",
  "5661": "Shoe Stores",
  "5699": "Apparel & Accessory Shops",
  "5712": "Furniture & Home Furnishings",
  "5722": "Household Appliance Stores",
  "5732": "Electronics Stores",
  "5811": "Caterers",
  "5812": "Eating Places & Restaurants",
  "5813": "Bars, Lounges & Nightclubs",
  "5814": "Fast Food Restaurants",
  "5912": "Drug Stores & Pharmacies",
  "5921": "Beer, Wine & Liquor Stores",
  "5941": "Sporting Goods Stores",
  "5942": "Book Stores",
  "5944": "Jewelry & Watch Stores",
  "5945": "Hobby, Toy & Game Shops",
  "5947": "Gift, Card & Novelty Shops",
  "5977": "Cosmetic Stores",
  "5992": "Florists",
  "5995": "Pet Shops & Supplies",
  "7011": "Hotels, Motels & Resorts",
  "7032": "Recreation Camps",
  "7033": "Trailer Parks & Campgrounds",
  "7210": "Laundry & Cleaning Services",
  "7211": "Laundries",
  "7216": "Dry Cleaners",
  "7217": "Carpet & Upholstery Cleaning",
  "7221": "Photographic Studios",
  "7230": "Beauty & Barber Shops",
  "7251": "Shoe Repair",
  "7276": "Tax Preparation Services",
  "7277": "Counseling Services",
  "7297": "Massage Services",
  "7298": "Health & Beauty Spas",
  "7299": "Personal Services",
  "7349": "Cleaning & Maintenance",
  "7372": "Computer Programming & Data",
  "7392": "Management & Consulting",
  "7512": "Car Rental Agencies",
  "7523": "Parking Lots & Garages",
  "7538": "Auto Service Shops",
  "7622": "Electronics Repair",
  "7832": "Cinemas & Theaters",
  "7922": "Theatrical & Ticket Agencies",
  "7929": "Bands & Entertainers",
  "7932": "Billiard & Pool Halls",
  "7991": "Tourist Attractions",
  "7992": "Public Golf Courses",
  "7994": "Video Game Arcades",
  "7995": "Betting & Casino Gaming",
  "7999": "Recreation Services",
  "8011": "Doctors & Physicians",
  "8021": "Dentists & Orthodontists",
  "8031": "Osteopaths",
  "8099": "Medical Services",
  "8111": "Legal Services & Attorneys",
  "8299": "Educational Services",
  "8911": "Architectural & Engineering",
  "8931": "Accounting & Bookkeeping",
};

export function getDomesticDebitDef(country: string): GroupDef {
  const map: Record<
    string,
    { buckets: string; cost: PriceSpec; current: PriceSpec }
  > = {
    NL: {
      buckets: "Maestro / V-Pay domestic debit",
      cost: { value: 0.05, valueType: "Fixed" },
      current: { value: 0.066, valueType: "Fixed" },
    },
    BE: {
      buckets: "Bancontact domestic debit",
      cost: { value: 0.06, valueType: "Fixed" },
      current: { value: 0.085, valueType: "Fixed" },
    },
    DE: {
      buckets: "Girocard domestic debit",
      cost: { value: 0.06, valueType: "Fixed" },
      current: { value: 0.09, valueType: "Fixed" },
    },
    FR: {
      buckets: "CB domestic debit",
      cost: { value: 0.05, valueType: "Fixed" },
      current: { value: 0.07, valueType: "Fixed" },
    },
    IT: {
      buckets: "PagoBancomat domestic debit",
      cost: { value: 0.3, valueType: "Percentage" },
      current: { value: 0.55, valueType: "Percentage" },
    },
  };
  const d = map[country] || map.NL;
  return { label: "Domestic Debit", ...d };
}

// Today's price for the country's domestic debit scheme, which is also the
// scheme's unit: Italy prices PagoBancomat as a percentage where the rest price
// per transaction, so the value and its type have to travel together.
export function getCurrentDomesticDebit(country: string): PriceSpec {
  return { ...getDomesticDebitDef(country).current };
}

export function getOfflineDefs(
  template: PricingTemplate,
  country: string
): Record<string, GroupDef> {
  const euBuckets =
    template === "europeanDebitSplit"
      ? "Domestic credit + European credit"
      : template === "domesticCreditSplit"
        ? "European debit & credit"
        : "Domestic credit + European debit & credit";
  return {
    domesticDebit: getDomesticDebitDef(country),
    domesticCredit: {
      label: "Domestic Credit",
      buckets: "Domestic credit cards",
      cost: { value: 0.45, valueType: "Percentage" },
      current: { value: 0.95, valueType: "Percentage" },
    },
    europeanDebit: {
      label: "European Debit",
      buckets: "EEA debit (non-domestic)",
      cost: { value: 0.22, valueType: "Percentage" },
      current: { value: 0.69, valueType: "Percentage" },
    },
    european: {
      label: "E.E.A Cards",
      buckets: euBuckets,
      cost: { value: 0.5, valueType: "Percentage" },
      current: { value: 1.39, valueType: "Percentage" },
    },
    international: {
      label: "Non-E.E.A & Corporate",
      buckets: "Commercial + international cards",
      cost: { value: 1.7, valueType: "Percentage" },
      current: { value: 2.39, valueType: "Percentage" },
    },
  };
}

export function getOnlineDefs(): Record<string, GroupDef> {
  return {
    onlineIdeal: {
      label: "iDEAL",
      buckets: "iDEAL bank transfers",
      cost: { value: 0.2, valueType: "Fixed" },
      current: { value: 0.29, valueType: "Fixed" },
    },
    onlineNonCorp: {
      label: "Consumer Cards",
      buckets: "Non-corporate credit cards",
      cost: { value: 1.2, valueType: "Percentage" },
      current: { value: 1.79, valueType: "Percentage" },
    },
    onlineCorp: {
      label: "Corporate Cards",
      buckets: "Corporate credit cards",
      cost: { value: 1.9, valueType: "Percentage" },
      current: { value: 2.59, valueType: "Percentage" },
    },
  };
}

export function getGroupOrder(
  channel: Channel,
  template: PricingTemplate
): string[] {
  if (channel === "online")
    return ["onlineIdeal", "onlineNonCorp", "onlineCorp"];
  if (template === "europeanDebitSplit")
    return ["domesticDebit", "europeanDebit", "european", "international"];
  if (template === "domesticCreditSplit")
    return ["domesticDebit", "domesticCredit", "european", "international"];
  return ["domesticDebit", "european", "international"];
}

export function getActiveDefs(
  channel: Channel,
  template: PricingTemplate,
  country: string
): Record<string, GroupDef> {
  return channel === "online"
    ? getOnlineDefs()
    : getOfflineDefs(template, country);
}

// What the proposed-price editor opens on: today's price for every group.
//
// It used to open on a second, higher set of prices — European cards at 1.49%
// against a list price of 1.39%, international at 2.69% against 2.39% — so the
// panel's headline rate was a proposal nobody had authored, reported above a
// green arrow, before anything on screen had been touched. Seeding from the
// list price means the tool states today until someone changes a number, and
// the gap it does show on load is the real one: list price against the net
// actually earned, which is the discount being given away.
//
// Both channels are seeded at once because switching channel or template must
// find a price already in place for groups the previous one didn't show.
// Templates differ only in how they label and group buckets, never in a group's
// price, so any template's figures seed all of them.
export function seedProposed(country: string): Record<string, PriceSpec> {
  const today = { ...getOfflineDefs("default", country), ...getOnlineDefs() };
  return Object.fromEntries(
    Object.entries(today).map(([group, def]) => [group, { ...def.current }])
  );
}

// Bucket-to-group mapping — keys match BucketNameField camelCase enum values from the server
export function bucketToGroup(
  channel: Channel,
  template: PricingTemplate
): Record<string, string> {
  if (channel === "online") {
    return {
      onlineIDeal: "onlineIdeal",
      onlineAmex: "onlineAmex",
      onlineCorporateCreditCards: "onlineCorp",
      onlineNonCorporateCreditCards: "onlineNonCorp",
      // A 16-bucket merchant's card payment stays on the bucket it landed in,
      // so its volume arrives on these twelve rather than the two above.
      onlineDomesticDebit: "onlineNonCorp",
      onlineDomesticCredit: "onlineNonCorp",
      onlineEeaDebit: "onlineNonCorp",
      onlineEuropeanCredit: "onlineNonCorp",
      onlineInternationalDebit: "onlineNonCorp",
      onlineInternationalCredit: "onlineNonCorp",
      onlineDomesticCorporateDebit: "onlineCorp",
      onlineDomesticCorporateCredit: "onlineCorp",
      onlineEuropeanCorporateDebit: "onlineCorp",
      onlineEuropeanCorporateCredit: "onlineCorp",
      onlineInternationalCorporateDebit: "onlineCorp",
      onlineInternationalCorporateCredit: "onlineCorp",
    };
  }
  const m: Record<string, string> = {
    domesticDebit: "domesticDebit",
    domesticCredit: "european",
    europeanCredit: "european",
    europeanDebit: "european",
    domesticCorporateDebit: "international",
    domesticCorporateCredit: "international",
    europeanCorporateDebit: "international",
    europeanCorporateCredit: "international",
    internationalDebit: "international",
    internationalCredit: "international",
    internationalCorporateDebit: "international",
    internationalCorporateCredit: "international",
    inPersonAmex: "international",
  };
  if (template === "europeanDebitSplit") {
    m.europeanDebit = "europeanDebit";
    m.domesticCredit = "european";
  }
  if (template === "domesticCreditSplit") {
    m.domesticCredit = "domesticCredit";
    m.europeanDebit = "european";
  }
  return m;
}

export function baseBucketShares(channel: Channel): Record<string, number> {
  if (channel === "online")
    return {
      onlineIDeal: 68,
      onlineNonCorporateCreditCards: 22,
      onlineCorporateCreditCards: 6,
      onlineAmex: 4,
    };
  return {
    domesticDebit: 56,
    domesticCredit: 7,
    domesticCorporateDebit: 1,
    domesticCorporateCredit: 2,
    europeanDebit: 8,
    europeanCredit: 9,
    europeanCorporateDebit: 1,
    europeanCorporateCredit: 2,
    internationalDebit: 2,
    internationalCredit: 3,
    internationalCorporateDebit: 1,
    internationalCorporateCredit: 1,
    inPersonAmex: 7,
  };
}

export const GROUP_PROFILES: Record<
  string,
  {
    benchOff: number;
    atvOff: number;
    benchOn: number;
    atvOn: number;
    tiltOff: Record<string, number>;
    tiltOn: Record<string, number>;
  }
> = {
  "Restaurants & Bars": {
    benchOff: 78000,
    atvOff: 34,
    benchOn: 9000,
    atvOn: 46,
    tiltOff: {},
    tiltOn: {},
  },
  "Retail Shops": {
    benchOff: 120000,
    atvOff: 41,
    benchOn: 60000,
    atvOn: 64,
    tiltOff: { domesticDebit: 1.12, internationalCredit: 0.8 },
    tiltOn: { onlineNonCorporateCreditCards: 1.2 },
  },
  "Service Providers": {
    benchOff: 42000,
    atvOff: 72,
    benchOn: 22000,
    atvOn: 115,
    tiltOff: { domesticCredit: 1.1 },
    tiltOn: { onlineNonCorporateCreditCards: 1.1 },
  },
  "Beauty & Barber": {
    benchOff: 26000,
    atvOff: 48,
    benchOn: 3000,
    atvOn: 55,
    tiltOff: { domesticDebit: 1.1 },
    tiltOn: {},
  },
  "Entertainment & Events": {
    benchOff: 90000,
    atvOff: 38,
    benchOn: 45000,
    atvOn: 55,
    tiltOff: { internationalCredit: 1.4 },
    tiltOn: { onlineNonCorporateCreditCards: 1.2 },
  },
  "Taxi & Transportation": {
    benchOff: 32000,
    atvOff: 18,
    benchOn: 12000,
    atvOn: 22,
    tiltOff: { domesticDebit: 1.2, internationalDebit: 1.2 },
    tiltOn: {},
  },
  Other: {
    benchOff: 150000,
    atvOff: 95,
    benchOn: 70000,
    atvOn: 160,
    tiltOff: {
      internationalCredit: 1.6,
      internationalDebit: 1.4,
      europeanCredit: 1.2,
      domesticDebit: 0.6,
    },
    tiltOn: { onlineCorporateCreditCards: 2.0, onlineIDeal: 0.8 },
  },
};

export function industryShares(
  group: string,
  channel: Channel
): Record<string, number> {
  const base = { ...baseBucketShares(channel) };
  const prof = GROUP_PROFILES[group] || GROUP_PROFILES["Restaurants & Bars"];
  const tilt = channel === "online" ? prof.tiltOn : prof.tiltOff;
  Object.keys(tilt).forEach((k) => {
    if (base[k] != null) base[k] *= tilt[k];
  });
  const sum = Object.values(base).reduce((a, b) => a + b, 0);
  Object.keys(base).forEach((k) => (base[k] = (base[k] / sum) * 100));
  return base;
}

// Friendly display names for the raw server bucket keys
export const BUCKET_LABELS: Record<string, string> = {
  domesticDebit: "Domestic debit",
  domesticCredit: "Domestic credit",
  domesticCorporateDebit: "Domestic corporate debit",
  domesticCorporateCredit: "Domestic corporate credit",
  europeanDebit: "European debit",
  europeanCredit: "European credit",
  europeanCorporateDebit: "European corporate debit",
  europeanCorporateCredit: "European corporate credit",
  internationalDebit: "International debit",
  internationalCredit: "International credit",
  internationalCorporateDebit: "International corporate debit",
  internationalCorporateCredit: "International corporate credit",
  inPersonAmex: "In-person Amex",
  onlineIDeal: "iDEAL",
  onlineAmex: "Online Amex",
  onlineCorporateCreditCards: "Online corporate cards",
  onlineNonCorporateCreditCards: "Online consumer cards",
  onlineDomesticDebit: "Online domestic debit",
  onlineDomesticCredit: "Online domestic credit",
  onlineEeaDebit: "Online EEA debit",
  onlineEuropeanCredit: "Online European credit",
  onlineInternationalDebit: "Online international debit",
  onlineInternationalCredit: "Online international credit",
  onlineDomesticCorporateDebit: "Online domestic corporate debit",
  onlineDomesticCorporateCredit: "Online domestic corporate credit",
  onlineEuropeanCorporateDebit: "Online European corporate debit",
  onlineEuropeanCorporateCredit: "Online European corporate credit",
  onlineInternationalCorporateDebit: "Online international corporate debit",
  onlineInternationalCorporateCredit: "Online international corporate credit",
};

// Reverse of bucketToGroup: the raw buckets that roll up into a given pricing group
export function groupBucketLabels(
  b2g: Record<string, string>,
  group: string
): string[] {
  return Object.entries(b2g)
    .filter(([, g]) => g === group)
    .map(([b]) => BUCKET_LABELS[b] || b);
}

export function groupVolShares(
  bucketShares: Record<string, number>,
  order: string[],
  b2g: Record<string, string>
): Record<string, number> {
  const out: Record<string, number> = {};
  order.forEach((g) => (out[g] = 0));
  Object.entries(bucketShares).forEach(([b, pct]) => {
    const g = b2g[b];
    if (g && out[g] != null) out[g] += pct;
  });
  const sum = order.reduce((a, g) => a + out[g], 0);
  if (sum > 0) order.forEach((g) => (out[g] = (out[g] / sum) * 100));
  return out;
}

function bucketsByGroup(b2g: Record<string, string>): Record<string, string[]> {
  const byGroup: Record<string, string[]> = {};
  Object.entries(b2g).forEach(([bucket, group]) => {
    (byGroup[group] ??= []).push(bucket);
  });
  return byGroup;
}

// The last bucket takes whatever is left so the pieces add back to `total`
// exactly. Dividing each one on its own drifts, and a row whose cards did not
// move has to come back as the weight that was typed, not a close relative.
function writeShare(
  out: Record<string, number>,
  buckets: string[],
  total: number,
  weightOf: (bucket: string) => number
): void {
  const weightSum = buckets.reduce((sum, bucket) => sum + weightOf(bucket), 0);
  let assigned = 0;
  buckets.forEach((bucket, index) => {
    if (index === buckets.length - 1) {
      out[bucket] = total - assigned;
      return;
    }
    const piece =
      weightSum > 0
        ? (total * weightOf(bucket)) / weightSum
        : total / buckets.length;
    out[bucket] = piece;
    assigned += piece;
  });
}

// Spread typed group weights across the cards inside each group. A group that
// already has card weights keeps their ratio and only the total moves, so a
// split made on another template is not flattened the next time a box is
// edited. Weights are whatever the user typed — volume or transaction count —
// and are not rescaled to 100 here.
export function absorbGroupWeights(
  groupWeights: Record<string, number>,
  b2g: Record<string, string>,
  industry: Record<string, number>,
  previous: Record<string, number> | null
): Record<string, number> {
  const byGroup = bucketsByGroup(b2g);
  const out: Record<string, number> = previous ? { ...previous } : {};
  Object.entries(groupWeights).forEach(([group, share]) => {
    const buckets = byGroup[group];
    if (!buckets?.length || share == null || Number.isNaN(share)) return;
    const prevSum = buckets.reduce(
      (sum, bucket) => sum + (out[bucket] || 0),
      0
    );
    if (prevSum > 0 && prevSum === share) return;
    const weightOf =
      prevSum > 0
        ? (bucket: string) => out[bucket] || 0
        : (bucket: string) => industry[bucket] || 0;
    writeShare(out, buckets, share, weightOf);
  });
  return out;
}

// Group weights for one template from card weights typed against another.
// A row is carried only when every card in it was part of what was typed, so
// a row the user never filled in stays absent and the usual mix shows through.
// A row whose cards did not move adds back to the weight that was typed; a row
// that was split shares that weight between the rows its cards moved to.
export function groupWeightsFromBuckets(
  bucketWeights: Record<string, number>,
  order: string[],
  b2g: Record<string, string>
): Record<string, number> {
  const byGroup = bucketsByGroup(b2g);
  const out: Record<string, number> = {};
  order.forEach((group) => {
    const buckets = byGroup[group];
    if (!buckets?.length) return;
    if (!buckets.every((bucket) => bucketWeights[bucket] != null)) return;
    out[group] = buckets.reduce(
      (sum, bucket) => sum + (bucketWeights[bucket] || 0),
      0
    );
  });
  return out;
}

// Relative average ticket per group (for txn share computation)
export const GROUP_ATV_REL: Record<string, number> = {
  domesticDebit: 0.55,
  domesticCredit: 1.0,
  europeanDebit: 0.7,
  european: 1.2,
  international: 2.1,
  onlineIdeal: 0.8,
  onlineNonCorp: 1.0,
  onlineCorp: 1.7,
};

// Count shares back to volume shares: the other direction of computeTxnShares,
// for a mix typed as "half our transactions are international". Half the
// transactions is more than half the volume there, because an international
// ticket is 2.1x a domestic debit one, and a percentage price earns on the
// volume — so reading a count share as a volume share understates every
// percentage-priced group.
export function computeVolShares(
  txnShares: Record<string, number>,
  order: string[]
): Record<string, number> {
  const raw: Record<string, number> = {};
  let s = 0;
  order.forEach((g) => {
    raw[g] = (txnShares[g] || 0) * (GROUP_ATV_REL[g] || 1);
    s += raw[g];
  });
  if (s <= 0) s = 1;
  const out: Record<string, number> = {};
  order.forEach((g) => (out[g] = (raw[g] / s) * 100));
  return out;
}

export function computeTxnShares(
  volShares: Record<string, number>,
  order: string[]
): Record<string, number> {
  const raw: Record<string, number> = {};
  let s = 0;
  order.forEach((g) => {
    raw[g] = (volShares[g] || 0) / (GROUP_ATV_REL[g] || 1);
    s += raw[g];
  });
  if (s <= 0) s = 1;
  const out: Record<string, number> = {};
  order.forEach((g) => (out[g] = (raw[g] / s) * 100));
  return out;
}

export function priceAmt(p: PriceSpec, gTPV: number, gTxns: number): number {
  return p.valueType === "Percentage"
    ? (p.value / 100) * gTPV
    : p.value * gTxns;
}

export function computeScenario(
  prices: Record<string, PriceSpec>,
  tpv: number,
  txns: number,
  order: string[],
  defs: Record<string, GroupDef>,
  volShares: Record<string, number>,
  txnShares: Record<string, number>,
  // The cost actually incurred per group, where the data says what it was.
  // Falling back to the table means a response without per-bucket cost still
  // prices a scenario, just not one that reconciles to the measured net.
  costs?: Record<string, PriceSpec>
) {
  let totalRev = 0,
    totalCost = 0;
  const rows = order.map((g) => {
    const vs = volShares[g] || 0,
      ts = txnShares[g] || 0;
    const gTPV = (tpv * vs) / 100,
      gTxns = (txns * ts) / 100;
    const revAmt = priceAmt(prices[g] || defs[g].current, gTPV, gTxns);
    const costAmt = priceAmt(costs?.[g] || defs[g].cost, gTPV, gTxns);
    totalRev += revAmt;
    totalCost += costAmt;
    return {
      g,
      volShare: vs,
      txnShare: ts,
      def: defs[g],
      price: prices[g] || defs[g].current,
      // The spec the cost amount above was actually taken from, so a caller
      // explaining the arithmetic quotes the rate that produced the number
      // rather than the table's.
      cost: costs?.[g] || defs[g].cost,
      revAmt,
      costAmt,
      net: revAmt - costAmt,
    };
  });
  return { rows, totalRev, totalCost, totalNet: totalRev - totalCost };
}

// Per-bucket figures as the insights response reports them, reduced to the four
// the pricing mix needs.
export interface BucketFigures {
  gross: number;
  txns: number;
  revenue: number;
  cost: number;
  // A reseller's cut and an ISV's cut of `revenue`, kept apart because the two
  // are separate contracts an agent negotiates separately. Zero for a direct
  // merchant, and zero on a response that does not report them — which is why
  // whether they were reported is tracked separately from their being 0.
  resellerMarkup: number;
  isvMarkup: number;
}

// What one partner takes out of a group's price, in that group's own unit, so
// it can be deducted the way a price is rather than as a cohort average.
export interface GroupMarkup {
  reseller: PriceSpec;
  isv: PriceSpec;
}

export interface GroupPricing {
  prices: Record<string, PriceSpec>;
  costs: Record<string, PriceSpec>;
  markups: Record<string, GroupMarkup>;
}

// What the merchants behind the current filters are actually charged, and what
// their volume actually costs, per pricing group.
//
// The panel used to apply a price list to measured volume and present the result
// as today's rate, which it is not: merchants hold individual deals, so the list
// is a different question from the bill. Revenue over volume is the price really
// charged; cost over volume the cost really incurred; and since the response's
// per-bucket revenue minus cost is its net revenue, a scenario priced this way
// reconciles to the measured figure rather than landing near it.
//
// The unit comes from the group's list price so the editor keeps showing what it
// was showing — a per-transaction fee stays per-transaction, which also needs
// real per-bucket transaction counts to divide by.
//
// A price is divided out against the exact quantity `computeScenario` will
// multiply it back by: the headline total, split by this group's share of the
// buckets that map to a group. Dividing by the group's own raw volume instead
// looks equivalent and is not, because the headline and the buckets need not
// agree — some transactions belong to no bucket group at all — and any gap
// between them would come back as a permanent error in the opening figure.
// Deriving against the same denominator cancels it instead.
//
// Returns null when the response carries no revenue to divide, which is both an
// older server and a cohort that billed nothing.
export function deriveGroupPricing(
  buckets: Record<string, BucketFigures>,
  order: string[],
  defs: Record<string, GroupDef>,
  b2g: Record<string, string>,
  tpv: number,
  txns: number
): GroupPricing | null {
  const totals: Record<string, BucketFigures> = {};
  order.forEach(
    (g) =>
      (totals[g] = {
        gross: 0,
        txns: 0,
        revenue: 0,
        cost: 0,
        resellerMarkup: 0,
        isvMarkup: 0,
      })
  );
  Object.entries(buckets).forEach(([bucket, f]) => {
    const group = b2g[bucket];
    if (!group || !totals[group]) return;
    totals[group].gross += f.gross || 0;
    totals[group].txns += f.txns || 0;
    totals[group].revenue += f.revenue || 0;
    totals[group].cost += f.cost || 0;
    totals[group].resellerMarkup += f.resellerMarkup || 0;
    totals[group].isvMarkup += f.isvMarkup || 0;
  });

  const billed = order.reduce((sum, g) => sum + totals[g].revenue, 0);
  if (billed <= 0) return null;

  const sum = (pick: (f: BucketFigures) => number) =>
    order.reduce((acc, g) => acc + pick(totals[g]), 0);
  const mappedGross = sum((f) => f.gross);
  const mappedTxns = sum((f) => f.txns);

  const prices: Record<string, PriceSpec> = {};
  const costs: Record<string, PriceSpec> = {};
  const markups: Record<string, GroupMarkup> = {};
  order.forEach((g) => {
    const {
      gross,
      txns: groupTxns,
      revenue,
      cost,
      resellerMarkup,
      isvMarkup,
    } = totals[g];
    const valueType = defs[g].current.valueType;
    // A per-transaction fee divides by transactions, a percentage by volume.
    const divisor =
      valueType === "Fixed"
        ? mappedTxns > 0
          ? (txns * groupTxns) / mappedTxns
          : 0
        : mappedGross > 0
          ? (tpv * gross) / mappedGross
          : 0;
    if (divisor <= 0) {
      // No volume in this group, or a per-transaction group whose counts the
      // server hasn't sent: nothing measured to state, so say the list price.
      prices[g] = { ...defs[g].current };
      costs[g] = { ...defs[g].cost };
      markups[g] = {
        reseller: { value: 0, valueType },
        isv: { value: 0, valueType },
      };
      return;
    }
    const scale = valueType === "Fixed" ? 1 : 100;
    prices[g] = { value: (scale * revenue) / divisor, valueType };
    costs[g] = { value: (scale * cost) / divisor, valueType };
    markups[g] = {
      reseller: { value: (scale * resellerMarkup) / divisor, valueType },
      isv: { value: (scale * isvMarkup) / divisor, valueType },
    };
  });
  return { prices, costs, markups };
}

export const TEMPLATE_OPTIONS = [
  { key: "default", label: "Default", sub: "3 groups" },
  { key: "europeanDebitSplit", label: "EU Debit Split", sub: "4 groups" },
  { key: "domesticCreditSplit", label: "Dom. Credit Split", sub: "4 groups" },
];

export const BREAKDOWN_COLORS = [
  "#6B4EFF",
  "#3F59FB",
  "#52c41a",
  "#fa8c16",
  "#1890ff",
  "#722ed1",
];
